import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TechnicalReportModal.css';
import TechniciansModal from './TechniciansModal';

const TechnicalReportModal = ({ report, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    type: 'instalacion',
    priority: 'media',
    phoneNumber: '',
    clientName: '',
    mikrowispId: '',
    clientAddress: '',
    serviceId: '',
    serviceType: '',
    servicePlan: '',
    title: '',
    description: '',
    technician: '',
    technicianPhone: '',
    scheduledDate: '',
    notes: '',
    internalNotes: '',
    tags: []
  });
  const [loading, setLoading] = useState(false);
  const [clientData, setClientData] = useState(null);
  const [searchingClient, setSearchingClient] = useState(false);
  const [showTechniciansModal, setShowTechniciansModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);

  useEffect(() => {
    loadTechnicians();
    if (report) {
      setFormData({
        type: report.type || 'instalacion',
        priority: report.priority || 'media',
        phoneNumber: report.phoneNumber || '',
        clientName: report.clientName || '',
        mikrowispId: report.mikrowispId || '',
        clientAddress: report.clientAddress || '',
        serviceId: report.serviceId || '',
        serviceType: report.serviceType || '',
        servicePlan: report.servicePlan || '',
        title: report.title || '',
        description: report.description || '',
        technician: report.technician || '',
        technicianPhone: report.technicianPhone || '',
        scheduledDate: report.scheduledDate ? new Date(report.scheduledDate).toISOString().slice(0, 16) : '',
        notes: report.notes || '',
        internalNotes: report.internalNotes || '',
        tags: report.tags || []
      });
    }
  }, [report]);

  const loadTechnicians = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/technicians', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setTechnicians(response.data.data.filter(t => t.isActive));
      }
    } catch (error) {
      console.error('Error cargando técnicos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchClient = async () => {
    if (!formData.mikrowispId || !formData.mikrowispId.trim()) {
      alert('Ingresa el ID de Mikrowisp del cliente');
      return;
    }

    try {
      setSearchingClient(true);
      const token = localStorage.getItem('token');
      
      // Limpiar el ID (remover espacios, ceros iniciales si es necesario)
      const cleanId = formData.mikrowispId.trim().replace(/^0+/, '');
      
      if (!cleanId || isNaN(parseInt(cleanId))) {
        alert('El ID de Mikrowisp debe ser un número válido');
        setSearchingClient(false);
        return;
      }
      
      console.log('🔍 Buscando cliente con ID:', cleanId);
      
      const response = await axios.post('/api/contacts/get-mikrowisp-client', {
        idcliente: parseInt(cleanId)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.client) {
        const client = response.data.client;
        setClientData(client);
        setFormData(prev => ({
          ...prev,
          clientName: client.nombre || prev.clientName,
          phoneNumber: client.movil || client.telefono || prev.phoneNumber,
          clientAddress: client.direccion_principal || prev.clientAddress,
          serviceType: client.servicios?.[0]?.tiposervicio || prev.serviceType,
          servicePlan: client.servicios?.[0]?.perfil || prev.servicePlan,
          serviceId: client.servicios?.[0]?.id?.toString() || prev.serviceId
        }));
      } else {
        alert('Cliente no encontrado en Mikrowisp');
      }
    } catch (error) {
      console.error('Error buscando cliente:', error);
      alert('Error al buscar cliente: ' + (error.response?.data?.error || error.message));
    } finally {
      setSearchingClient(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.phoneNumber || !formData.clientName || !formData.title || !formData.description) {
      alert('Por favor completa todos los campos requeridos');
      return;
    }

    setLoading(true);
    onSave(formData);
    setLoading(false);
  };

  const handleTagAdd = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, e.target.value.trim()]
      }));
      e.target.value = '';
    }
  };

  const handleTagRemove = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  return (
    <div className="technical-report-modal-overlay" onClick={onClose}>
      <div className="technical-report-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{report ? 'Editar Reporte Técnico' : 'Nuevo Reporte Técnico'}</h2>
          <button className="btn-close-modal" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="technical-report-form">
          <div className="form-section">
            <h3>Información del Cliente</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>ID Mikrowisp *</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    name="mikrowispId"
                    value={formData.mikrowispId}
                    onChange={handleChange}
                    placeholder="Ej: 513"
                  />
                  <button
                    type="button"
                    onClick={handleSearchClient}
                    disabled={searchingClient}
                    className="btn-search-client"
                  >
                    {searchingClient ? 'Buscando...' : '🔍 Buscar'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Teléfono *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="8091234567"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Nombre del Cliente *</label>
                <input
                  type="text"
                  name="clientName"
                  value={formData.clientName}
                  onChange={handleChange}
                  placeholder="Nombre completo"
                  required
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  name="clientAddress"
                  value={formData.clientAddress}
                  onChange={handleChange}
                  placeholder="Dirección del cliente"
                />
              </div>
            </div>

            {clientData && clientData.servicios && clientData.servicios.length > 0 && (
              <div className="form-row">
                <div className="form-group">
                  <label>Servicio</label>
                  <select
                    name="serviceId"
                    value={formData.serviceId}
                    onChange={handleChange}
                  >
                    <option value="">Seleccionar servicio</option>
                    {clientData.servicios.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.perfil || service.tiposervicio} - {service.ip || 'Sin IP'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <h3>Información del Reporte</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Tipo *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="instalacion">Instalación</option>
                  <option value="averia">Avería</option>
                  <option value="mantenimiento">Mantenimiento</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div className="form-group">
                <label>Prioridad *</label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                >
                  <option value="baja">Baja (3 horas)</option>
                  <option value="media">Media (2 horas)</option>
                  <option value="alta">Alta (1 hora)</option>
                  <option value="urgente">Urgente (30 minutos)</option>
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  El tiempo límite se asigna automáticamente según la prioridad
                </small>
              </div>
            </div>

            <div className="form-group">
              <label>Título *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Ej: Instalación en sector Los Alcarrizos"
                required
              />
            </div>

            <div className="form-group">
              <label>Descripción *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe el problema o trabajo a realizar..."
                rows="4"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Información del Técnico</h3>
            
            <div className="form-row">
            <div className="form-group">
              <label>Técnico Asignado</label>
              <div className="technician-selector">
                <select
                  name="technician"
                  value={formData.technician}
                  onChange={(e) => {
                    const selectedTech = technicians.find(t => t.name === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      technician: e.target.value,
                      technicianPhone: selectedTech ? selectedTech.phone : prev.technicianPhone
                    }));
                  }}
                >
                  <option value="">Seleccionar técnico...</option>
                  {technicians.map(tech => (
                    <option key={tech._id} value={tech.name}>
                      {tech.name} - {tech.phone}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowTechniciansModal(true)}
                  className="btn-manage-technicians-small"
                >
                  👷 Gestionar
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Teléfono del Técnico</label>
              <input
                type="tel"
                name="technicianPhone"
                value={formData.technicianPhone}
                onChange={handleChange}
                placeholder="8091234567"
              />
            </div>
            </div>

            <div className="form-group">
              <label>Fecha Programada</label>
              <input
                type="datetime-local"
                name="scheduledDate"
                value={formData.scheduledDate}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Notas</h3>
            
            <div className="form-group">
              <label>Notas (visibles para el cliente)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Notas que el cliente puede ver..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Notas Internas</label>
              <textarea
                name="internalNotes"
                value={formData.internalNotes}
                onChange={handleChange}
                placeholder="Notas internas (no visibles para el cliente)..."
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Etiquetas</label>
              <input
                type="text"
                placeholder="Presiona Enter para agregar etiqueta"
                onKeyPress={handleTagAdd}
              />
              <div className="tags-container">
                {formData.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="tag-remove"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onClose} className="btn-cancel">
              Cancelar
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? 'Guardando...' : report ? 'Actualizar' : 'Crear Reporte'}
            </button>
          </div>
        </form>
      </div>

      {/* Modal para gestionar técnicos */}
      <TechniciansModal
        isOpen={showTechniciansModal}
        onClose={() => {
          setShowTechniciansModal(false);
          loadTechnicians();
        }}
        onTechnicianSelect={(technician) => {
          setFormData(prev => ({
            ...prev,
            technician: technician.name,
            technicianPhone: technician.phone
          }));
          setShowTechniciansModal(false);
        }}
      />
    </div>
  );
};

export default TechnicalReportModal;

